import React, { useEffect, useRef, useState } from 'react';
import { Mic, Pause, Play, Square, X } from 'lucide-react';

/**
 * In-browser voice instruction recorder with timer, level meter, pause/resume.
 */
export function VoiceRecordPanel({ open, onCancel, onComplete }) {
  const [seconds, setSeconds] = useState(0);
  const [paused, setPaused] = useState(false);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState('');

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);

  const cleanup = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try {
      mediaRecorderRef.current?.stream?.getTracks?.().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    try {
      streamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    try {
      audioCtxRef.current?.close?.();
    } catch {
      /* ignore */
    }
    mediaRecorderRef.current = null;
    streamRef.current = null;
    analyserRef.current = null;
    audioCtxRef.current = null;
  };

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    (async () => {
      setError('');
      setSeconds(0);
      setPaused(false);
      setLevel(0);
      chunksRef.current = [];
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError('Microphone not available in this browser');
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          audioCtxRef.current = ctx;
          const source = ctx.createMediaStreamSource(stream);
          const analyser = ctx.createAnalyser();
          analyser.fftSize = 256;
          source.connect(analyser);
          analyserRef.current = analyser;
          const data = new Uint8Array(analyser.frequencyBinCount);
          const tick = () => {
            if (!analyserRef.current) return;
            analyserRef.current.getByteFrequencyData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i += 1) sum += data[i];
            setLevel(Math.min(1, sum / (data.length * 180)));
            rafRef.current = requestAnimationFrame(tick);
          };
          tick();
        } catch {
          /* level meter optional */
        }

        let options = {};
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported) {
          if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
            options = { mimeType: 'audio/webm;codecs=opus' };
          } else if (MediaRecorder.isTypeSupported('audio/webm')) {
            options = { mimeType: 'audio/webm' };
          }
        }
        const recorder = new MediaRecorder(stream, options);
        mediaRecorderRef.current = recorder;
        recorder.ondataavailable = (e) => {
          if (e.data?.size) chunksRef.current.push(e.data);
        };
        recorder.start(250);
        timerRef.current = setInterval(() => {
          setSeconds((s) => s + 1);
        }, 1000);
      } catch {
        setError('Could not access microphone. Check browser permissions.');
      }
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [open]);

  const formatTimer = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  };

  const pause = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === 'recording') {
      rec.pause();
      setPaused(true);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const resume = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === 'paused') {
      rec.resume();
      setPaused(false);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
  };

  const stop = () => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === 'inactive') {
      onCancel?.();
      return;
    }
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
      cleanup();
      onComplete?.(blob, { durationSeconds: seconds });
    };
    try {
      rec.stop();
    } catch {
      cleanup();
      onCancel?.();
    }
  };

  const cancel = () => {
    cleanup();
    onCancel?.();
  };

  if (!open) return null;

  const bars = Array.from({ length: 16 }, (_, i) => {
    const h = Math.max(0.12, Math.min(1, level * (0.4 + ((i * 7) % 10) / 10)));
    return h;
  });

  return (
    <div
      className="rounded-xl border border-red-400/30 bg-red-500/10 p-3 space-y-3"
      data-testid="voice-record-panel"
      role="region"
      aria-label="Voice recording"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-red-200 text-[12px] font-medium">
          <span className={`w-2 h-2 rounded-full bg-red-400 ${paused ? '' : 'animate-pulse'}`} />
          {paused ? 'Paused' : 'Recording voice instruction'}
        </div>
        <span className="font-mono text-sm text-white tabular-nums">{formatTimer(seconds)}</span>
      </div>

      {error ? (
        <p className="text-[12px] text-red-200">{error}</p>
      ) : (
        <div className="flex items-end gap-0.5 h-8 px-1" aria-hidden>
          {bars.map((h, i) => (
            <span
              key={i}
              className="flex-1 rounded-sm bg-red-300/80 transition-[height] duration-75"
              style={{ height: `${h * 100}%` }}
            />
          ))}
        </div>
      )}

      <p className="text-[10px] text-[#9CA3AF] leading-relaxed">
        Twinnee treats this as user intent / source material — not automatically as final narration.
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        {!paused ? (
          <button
            type="button"
            onClick={pause}
            disabled={Boolean(error)}
            className="px-2.5 py-1.5 rounded-full text-[10px] font-semibold border border-white/15 bg-white/[0.06] text-white flex items-center gap-1 cursor-pointer"
          >
            <Pause className="w-3 h-3" /> Pause
          </button>
        ) : (
          <button
            type="button"
            onClick={resume}
            className="px-2.5 py-1.5 rounded-full text-[10px] font-semibold border border-white/15 bg-white/[0.06] text-white flex items-center gap-1 cursor-pointer"
          >
            <Play className="w-3 h-3" /> Resume
          </button>
        )}
        <button
          type="button"
          onClick={stop}
          disabled={Boolean(error)}
          className="px-2.5 py-1.5 rounded-full text-[10px] font-semibold border border-red-400/40 bg-red-500/20 text-red-100 flex items-center gap-1 cursor-pointer"
        >
          <Square className="w-3 h-3" /> Stop
        </button>
        <button
          type="button"
          onClick={cancel}
          className="px-2.5 py-1.5 rounded-full text-[10px] font-semibold border border-white/10 text-[#9CA3AF] flex items-center gap-1 cursor-pointer hover:text-white"
        >
          <X className="w-3 h-3" /> Cancel
        </button>
        <span className="text-[10px] text-[#6B7280] flex items-center gap-1 ml-auto">
          <Mic className="w-3 h-3" /> Mic live
        </span>
      </div>
    </div>
  );
}

export default VoiceRecordPanel;
